/**
 * The route guards: `RequireAuth` for a session, `RequirePermission` for one
 * that also holds something.
 *
 * Both are about **navigation**, not access. They stop somebody landing on a
 * screen that would be nothing but an error; they are not what keeps them out of
 * the data. The server does that, on every request, whatever the router allowed.
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from './useAuth';
import { NotPermitted } from '../components/NotPermitted';
import ForcedPasswordChangePage from '../pages/ForcedPasswordChangePage';

/** Fills the viewport while the first `GET /auth/me` is still in flight. */
function Booting() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

/**
 * Sends anyone without a session to the sign-in screen.
 *
 * The attempted location rides along in router state, so signing in lands where
 * the user was going rather than dumping them on the dashboard — which matters
 * most for the case this exists for: a bookmarked deep link.
 *
 * A user an administrator has told to change their password gets the change
 * form instead of the shell, whatever the URL. Not during an impersonation:
 * the administrator does not know the password, and the server lets them past.
 */
export function RequireAuth() {
  const { user, impersonator, initialising } = useAuth();
  const location = useLocation();

  if (initialising) return <Booting />;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user.mustChangePassword && !impersonator) return <ForcedPasswordChangePage />;

  return <Outlet />;
}

export interface RequirePermissionProps {
  /** Holding **any** of these is enough. */
  anyOf: string[];
  /** Rendered instead of the outlet when the caller holds none of them. */
  children?: ReactNode;
}

/**
 * Renders a plain "you cannot see this" rather than redirecting.
 *
 * A redirect would be a lie by omission: the page exists, the user simply may
 * not open it, and bouncing them to the dashboard leaves them re-clicking the
 * same link wondering what broke.
 */
export function RequirePermission({ anyOf, children }: RequirePermissionProps) {
  const { canAny } = useAuth();

  if (!canAny(...anyOf)) {
    return <NotPermitted permissions={anyOf} />;
  }

  return <>{children ?? <Outlet />}</>;
}
